import { Title } from '@solidjs/meta';
import { useParams } from '@solidjs/router';
import { createEffect, createResource, createSignal, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { BreadCrumbs } from '../../components/breadcrumbs/breadcrumbs';
import { Container } from '../../components/layout/container/container';
import { Header } from '../../components/layout/header/header';
import { Screen } from '../../components/layout/screen/screen';
import { Product } from '../../components/products/product';
import { Product as ProductType } from '../../components/products/product-types.d';
import { getOriginProductsQuery, getProductQuery } from '../../graphql/queries/products';
import { multiSearchQuery } from '../../graphql/queries/registration-number-check';
import { MultiSearchResultType } from '../product-category/product-category-container';

export const ProductsContainer = () => {
    const params = useParams();
    const { createCachedResource, graphQlClient } = useContext(AppContext);
    const [product, setProduct] = createSignal<ProductType | null>(null);

    const [originProducts] = createCachedResource(getOriginProductsQuery);
    const [productData] = createCachedResource(getProductQuery, () => ({ slug: params.productSlug }));

    const [regNos] = createResource(product, async (args) => {
        const slug = args?.slug;
        if (!slug) {
            return [];
        }

        const query = (await graphQlClient(multiSearchQuery, { productSlugs: [slug] })) as { multiSearch: MultiSearchResultType[] };
        return Array.isArray(query.multiSearch) ? query.multiSearch : [];
    });

    createEffect(() => {
        if (productData()?.product) {
            setProduct(productData().product);
        }
    });

    const links = () => {
        if (!product()) {
            return [];
        }

        const productCategory = product()?.categories[0];

        if (!productCategory) {
            return [
                {
                    text: 'Produkter',
                    url: '/kategorier',
                },
            ];
        }

        return [
            {
                text: 'Produkter',
                url: '/kategorier',
            },
            {
                text: productCategory.name,
                url: `/kategorier/${productCategory.slug}`,
            },
        ];
    };

    const header = () => {
        if (!product()) {
            return '';
        }

        const productCategory = product()?.categories[0];

        return `${product()?.name} - ${productCategory?.name || ''}`;
    };

    return (
        <Screen>
            <BreadCrumbs links={links()} prompt="Produkter" />
            <Title>{`${product()?.name || ''} | Produkter - Gullvikspraktikan`}</Title>
            <Header header={header()} />
            <Container>
                <Product productData={productData} regNos={regNos} originProducts={originProducts} showHeader />
            </Container>
        </Screen>
    );
};
